import type { NavConfirmation, ProcessedActions, SkippedAction } from "~/helpers/aiChat/fernieActions";
import type { FernieAction } from "~/store/fernieActionSlice";

import { ApplicationEnum } from "~/@types/enums";
import { resolveDeepLinkFiltersFromPath } from "~/helpers/aiChat/gridDeepLinkFilters";
import { destinationLabel, getDestinationIndex } from "~/helpers/aiChat/navigationDestinations";
import { extractRecordReference, reachedRecord } from "~/helpers/aiChat/requestedDestination";
import localizedText from "~/helpers/localizedText";
import i18n from "~/i18n/i18n";
import { getFlatPageMapping, getPageInfo } from "~/locationMaps";
import { getWalkthrough } from "~/walkthroughs/registry";

const labelForPath = (path: string): string | null => {
	const pathname = path.trim().split(/[?#]/)[0];
	for (const application of Object.values(ApplicationEnum)) {
		const info = getPageInfo(pathname, getFlatPageMapping(application));
		if (info?.label)
			return info.title ? localizedText(info.title, info.titleKey) : localizedText(info.label, info.labelKey);
	}
	const destination = getDestinationIndex().find((entry) => entry.path === pathname);
	return destination ? destinationLabel(destination) : null;
};

const navigationFailed = (outcome: ProcessedActions): boolean =>
	outcome.skipped.some((item) => item.type === "navigateTo");

const describeDeepLinkFilters = (path: string): string | null => {
	const filters = resolveDeepLinkFiltersFromPath(path);
	if (filters.length === 0) return null;
	return joinClauses(filters.map((filter) => `${filter.label}: ${filter.valueLabels.join(", ")}`));
};

const describeNavigation = (action: Extract<FernieAction, { type: "navigateTo" }>): string | null => {
	const record = extractRecordReference(action.requestedDestination);
	if (record && reachedRecord(record, action.path, action.reachedDestination)) return `opened ${record.reference}`;
	const label = labelForPath(action.path);
	if (!label) return null;
	if (record) return `opened the ${label} list — I couldn't open ${record.reference} directly; it's in the list`;
	const filterClause = describeDeepLinkFilters(action.path);
	if (filterClause) return `opened ${label} filtered to ${filterClause}`;
	return `opened ${label}`;
};

const describeWalkthroughOutcome = (action: Extract<FernieAction, { type: "startWalkthrough" }>): string | null => {
	if (action.outcome === "completed") {
		const titleKey = getWalkthrough(action.tourId)?.titleKey;
		return titleKey ? `walked you through how to ${i18n.t(titleKey).toLowerCase()} ✓` : "walked you through it ✓";
	}
	if (action.outcome === "abandoned") return "stopped the walkthrough early";
	return null;
};

const describeAction = (action: FernieAction): string | null => {
	switch (action.type) {
		case "navigateTo":
			return describeNavigation(action);
		case "applyGridFilter":
			return "applied the filter you asked for";
		case "clearGridFilter":
			return "cleared the grid filters";
		case "setGridSearch":
			return action.searchText ? `filtered it to "${action.searchText}"` : "cleared the grid search";
		case "openSchedulePanel":
			return action.reachedDestination ? `opened job ${action.jobNumber}` : null;
		case "openReport":
			return action.reachedDestination ? `opened the "${action.reportName}" report` : null;
		case "openPanel":
			return action.panel === "drawer" ? "opened the navigation drawer" : "opened the panel";
		case "closePanel":
			return action.panel === "drawer" ? "closed the navigation drawer" : "closed the panel";
		case "startWalkthrough":
			return describeWalkthroughOutcome(action);
	}
};

const joinClauses = (clauses: string[]): string => {
	if (clauses.length === 1) return clauses[0];
	const head = clauses.slice(0, -1).join(", ");
	return `${head} and ${clauses[clauses.length - 1]}`;
};

const capitalize = (value: string): string => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

const describeSkipped = (skipped: SkippedAction[]): string | null => {
	if (skipped.length === 0) return null;
	return `I ${joinClauses(skipped.map((item) => `couldn't ${item.reason}`))}.`;
};

const buildActionFeedback = (outcome: ProcessedActions): string | null => describeSkipped(outcome.skipped);

const buildConfirmationPrompt = (confirmations: NavConfirmation[]): string | null => {
	const first = confirmations[0];
	if (!first || first.candidates.length === 0) return null;
	if (first.candidates.length === 1) return `Did you mean ${first.candidates[0].label}?`;
	return "Did you mean one of these?";
};

const buildExecutionFeedback = (executed: FernieAction[]): string | null => {
	const clauses = executed.map(describeAction).filter((clause): clause is string => clause !== null);
	if (clauses.length === 0) return null;
	return capitalize(`I've ${joinClauses(clauses)}.`);
};

const OVER_CLAIM_PATTERN =
	/\b(i've|i have)\s+(open(ed)?|navigat(ed|ing)|took|taken|brought|filter(ed)?|search(ed)?)\b|\byou(?:'re| are) now\b|\blet me take you to\b|\b(already )?got you on the\b.*\bpage\b|\btaking you to\b|\bsend(ing)? you (over )?to\b/i;

const isOverClaim = (answer: string): boolean => OVER_CLAIM_PATTERN.test(answer);

const composeFeedback = (answer: string, derivedFeedback: string): string => {
	const trimmed = answer.trim();
	if (!trimmed || isOverClaim(trimmed)) return derivedFeedback;
	return `${trimmed}\n\n${derivedFeedback}`;
};

export {
	buildActionFeedback,
	buildConfirmationPrompt,
	buildExecutionFeedback,
	composeFeedback,
	describeAction,
	isOverClaim,
	labelForPath,
	navigationFailed
};
export default buildActionFeedback;
